<script lang="ts">
import { defineComponent, ref } from "vue";
import formatUtils from "@/businesslogic/utils/formatUtils";
import IconSprite from "@/components/IconSprite.vue";
import Logger from "js-logger";
import storeUtils from "@/businesslogic/utils/storeUtils";
import { useFormStore } from "@/businesslogic/stores/formStore";

export default defineComponent({
  name: "Produktmodul",
  components: { IconSprite },
  props: {
    produktmodule: Array,
    zeigeIcons: {
      type: Boolean,
      default: true,
    },
  },
  emits: ["change"],

  setup(props, { emit }) {
    const hidden = ref(true);
    const piniaStore = useFormStore();
    const { formatCurrency } = formatUtils();
    const { istFhkVerkaufsprodukt } = storeUtils();

    const zeigeElementarHinweis = (modul: any) => {
      return (
        modul.modulId === "elementar" &&
        (piniaStore.state.zuersZone.erdbebenzone || piniaStore.state.zuersZone.zone === "4")
      );
    };

    function infoText(modul: any) {
      if (piniaStore.state.isMakler && modul.infoMakler) {
        return modul.infoMakler;
      }

      return modul.info;
    }

    function zeigeInfo(modul: any) {
      modul.show = !modul.show;
    }

    function calculateIndex(preisId: string) {
      for (const modul of piniaStore.state.modulPreise) {
        if (modul.preisName == preisId) {
          return modul.preis;
        }
      }
    }

    const zeigeZuersZone = (modul: any) => {
      Logger.debug("zeigeZuersZone", modul.modulId, piniaStore.state.isMakler, piniaStore.state.zuersZone.zone);
      return modul.modulId === "elementar" && piniaStore.state.isMakler && piniaStore.state.zuersZone.zone;
    };

    const zuersZoneUnbekannt = (modul: any) => {
      return modul.modulId === "elementar" && piniaStore.state.isMakler && piniaStore.state.zuersZone.zone === "";
    };

    const emitChange = (modulId: any, value: any) => {
      emit("change", modulId, value);
    };

    return {
      hidden,
      zeigeInfo,
      formatCurrency,
      piniaStore,
      props,
      calculateIndex,
      infoText,
      zeigeZuersZone,
      zeigeElementarHinweis,
      zuersZoneUnbekannt,
      istFhkVerkaufsprodukt,
      emitChange,
    };
  },
});
</script>

<template>
  <div v-for="modul in props.produktmodule" :key="modul.modulId">
    <div
      class="o-grid"
      v-if="modul.anzeigen"
      v-bind:class="{
        'u-mar-t-1': modul.modulPrefix === '',
        'u-mar-t-2': modul.modulPrefix !== '',
      }"
    >
      <div class="o-grid__cell o-grid__cell--12 o-grid__cell--3-480 u-text-align-right--480 u-form-grid-nospace-y">
        <h2 v-if="modul.modulPrefix !== ''" class="u-form-heading unset-text-align">
          {{ modul.modulPrefix }}
        </h2>
      </div>
      <div
        class="o-grid__cell o-grid__cell--12 o-grid__cell--9-480 o-grid__cell--8-768 o-grid__cell--7-1024 o-grid__cell--6-1200"
        v-bind:class="{
          'u-form-grid-nospace-y': modul.modulPrefix === '',
          'u-form-grid-lowspace-y': modul.modulPrefix !== '',
        }"
      >
        <div
          class="gev19-optional-module"
          v-bind:class="{
            'gev19-optional-module--enabled': piniaStore.state.tarifinformationen[modul.modulId],
            'gev19-optional-module--disabled': modul.aktiv === false,
          }"
        >
          <div>
            <div class="c-form-control-checkbox">
              <input
                v-model="piniaStore.state.tarifinformationen[modul.modulId]"
                :id="modul.modulId"
                class="c-form-control-checkbox__input"
                type="checkbox"
                tabindex="0"
                :disabled="!modul.aktiv"
                v-on:change="emitChange(modul.modulId, piniaStore.state.tarifinformationen[modul.modulId])"
              />
              <label :for="modul.modulId" class="c-form-control-checkbox__label">
                <icon-sprite
                  icon-name="icon-check-mark"
                  css-klassen="c-form-control-checkbox__icon"
                  :ist-klein="true"
                />
                <span
                  v-if="!piniaStore.state.isMakler"
                  class="c-form-control-checkbox__label-text"
                  v-html="modul.name"
                />
                <span
                  v-if="piniaStore.state.isMakler"
                  class="c-form-control-checkbox__label-text"
                  v-html="modul.nameMakler"
                />
                <span
                  v-if="!modul.aktiv"
                  v-html="modul.inaktivText"
                  class="gev19-optional-module-content__inactive-text"
                />
              </label>
              <span
                v-if="modul.info.length"
                class="gev19__contexthelp-open"
                title="Hilfe anzeigen"
                @click="zeigeInfo(modul)"
              >
              </span>
            </div>
            <div class="gev19-optional-module-content">
              <div class="gev19-optional-module-content-icon" v-if="modul.icon.length && zeigeIcons">
                <icon-sprite :icon-name="modul.icon" :ist-gross="true" />
              </div>
              <div
                class="gev19-optional-module-content-text u-mar-t-1"
                v-if="!istFhkVerkaufsprodukt(piniaStore.state) && zeigeElementarHinweis(modul)"
              >
                <slot name="vhvElementarHinweis"></slot>
                <slot v-if="zuersZoneUnbekannt(modul)" name="unbekannteZuersZoneHinweis"></slot>
                <div class="zuerstext" v-if="zeigeZuersZone(modul)">
                  <ul class="c-list">
                    <li class="c-list__item">
                      <strong>Ermittelte Zonen: </strong>{{ piniaStore.state.zuersZone.zone }} ZÜRS,
                      {{ piniaStore.state.zuersZone.starkregen }} Starkregen
                    </li>
                  </ul>
                </div>
              </div>
              <div
                class="gev19-optional-module-content-text"
                v-if="istFhkVerkaufsprodukt(piniaStore.state) && zeigeElementarHinweis(modul)"
              >
                <slot name="vhvElementarHinweis"></slot>
              </div>
              <div class="gev19-optional-module-content-text" v-if="!zeigeElementarHinweis(modul)">
                <span v-html="modul.body"></span>
                <slot v-if="zuersZoneUnbekannt(modul)" name="unbekannteZuersZoneHinweis"></slot>
                <div class="zuerstext" v-if="zeigeZuersZone(modul)">
                  <ul class="c-list">
                    <li class="c-list__item">
                      <strong>Ermittelte Zonen: </strong>{{ piniaStore.state.zuersZone.zone }} ZÜRS,
                      {{ piniaStore.state.zuersZone.starkregen }} Starkregen
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            <slot name="vhvFahrrad" v-if="modul.modulId === 'fahrradschutz'"></slot>
            <div class="gev19-optional-module-premium u-text-align-right">
              {{ modul.preisId ? formatCurrency(calculateIndex(modul.preisId)) : "beitragspflichtig" }}
            </div>
            <slot
              name="vhvElementar"
              v-if="modul.modulId === 'elementar' && piniaStore.state.tarifinformationen.elementar"
            ></slot>
          </div>
          <div
            v-if="modul.show"
            class="gev19__contexthelp-text"
            @click="zeigeInfo(modul)"
            v-html="infoText(modul)"
          ></div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.vhvElementarHinweis {
  padding-top: 1rem;
}

.hervorheben {
  font-weight: bold;
}
</style>
